<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AiAgentsAPI from '../../../../../api/aiAgents';

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  show: { type: Boolean, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const step = ref(1);
const isCreating = ref(false);
const createdAgentId = ref(null);

const selectedSpecialty = ref(null);
const agentName = ref('');
const agentDescription = ref('');
const selectedCatalogType = ref('products');
const searchQuery = ref('');
const isSavingKnowledge = ref(false);
const industryForm = ref({
  specialization: '',
  businessName: '',
  website: '',
  description: '',
});

const specialtyGroups = computed(() => [
  {
    key: 'custom',
    label: t('AI_AGENTS.WIZARD.GROUPS.CUSTOM'),
    items: [
      {
        value: 'custom',
        icon: 'i-lucide-sliders-horizontal',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.CUSTOM.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.CUSTOM.DESCRIPTION'),
        catalogType: null,
        template: null,
      },
    ],
  },
  {
    key: 'support',
    label: t('AI_AGENTS.WIZARD.GROUPS.SUPPORT'),
    items: [
      {
        value: 'customer_support',
        icon: 'i-lucide-headphones',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUPPORT.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUPPORT.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUPPORT.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUPPORT.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUPPORT.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Resolver duda',
                instruction:
                  'Respondé la consulta del cliente usando la información disponible. Si no tenés la respuesta, ofrecé transferir a un humano.',
              },
            ],
          },
        },
      },
      {
        value: 'help_desk',
        icon: 'i-lucide-life-buoy',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.HELP_DESK.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.HELP_DESK.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HELP_DESK.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HELP_DESK.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HELP_DESK.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.6,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Recibir ticket',
                instruction:
                  'Recibí la consulta del usuario y clasificá el tipo de problema.',
              },
              {
                type: 'action',
                title: 'Solución',
                instruction:
                  'Ofrecé una solución paso a paso. Si no se resuelve, ofrecé transferir a un agente humano.',
              },
            ],
          },
        },
      },
      {
        value: 'technical_support',
        icon: 'i-lucide-wrench',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.TECHNICAL_SUPPORT.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.TECHNICAL_SUPPORT.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.TECHNICAL_SUPPORT.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.TECHNICAL_SUPPORT.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.TECHNICAL_SUPPORT.WELCOME_MESSAGE'
          ),
          response_level: 4,
          temperature: 0.5,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Diagnóstico',
                instruction:
                  'Pedí al usuario que describa el problema técnico con detalle.',
              },
              {
                type: 'action',
                title: 'Solución',
                instruction:
                  'Proporcioná una solución técnica paso a paso. Si no se resuelve, ofrecé transferir a soporte especializado.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'sales',
    label: t('AI_AGENTS.WIZARD.GROUPS.SALES'),
    items: [
      {
        value: 'sales',
        icon: 'i-lucide-trending-up',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.SALES.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.SALES.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t('AI_AGENTS.WIZARD.SPECIALTIES.SALES.SYSTEM_PROMPT'),
          instructions: t('AI_AGENTS.WIZARD.SPECIALTIES.SALES.INSTRUCTIONS'),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.SALES.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá qué producto o servicio le interesa.',
              },
              {
                type: 'action',
                title: 'Cualificación',
                instruction:
                  'Hacé preguntas para entender las necesidades del cliente.',
              },
              {
                type: 'action',
                title: 'Propuesta',
                instruction:
                  'Presentá la mejor opción según lo que necesita el cliente.',
              },
              {
                type: 'action',
                title: 'Cierre',
                instruction:
                  'Invitá al cliente a concretar la compra o agendar una demo.',
              },
            ],
          },
        },
      },
      {
        value: 'lead_tracking',
        icon: 'i-lucide-target',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.LEAD_TRACKING.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.LEAD_TRACKING.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LEAD_TRACKING.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LEAD_TRACKING.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LEAD_TRACKING.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Identificación',
                instruction: 'Pedí el nombre, email y teléfono del interesado.',
              },
              {
                type: 'action',
                title: 'Necesidades',
                instruction:
                  'Preguntá qué producto o servicio le interesa y en qué etapa de decisión está.',
              },
              {
                type: 'action',
                title: 'Seguimiento',
                instruction:
                  'Ofrecé agendar una llamada o enviar más información.',
              },
            ],
          },
        },
      },
      {
        value: 'ecommerce',
        icon: 'i-lucide-shopping-cart',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.ECOMMERCE.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.ECOMMERCE.DESCRIPTION'),
        catalogType: 'products',
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.ECOMMERCE.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.ECOMMERCE.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.ECOMMERCE.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction: 'Saluda al cliente y preguntá qué producto busca.',
              },
              {
                type: 'product_search',
                title: 'Buscar producto',
                product_query: '{{user_input}}',
                include_media: true,
              },
              {
                type: 'action',
                title: 'Recomendación',
                instruction:
                  'Recomendá el producto más adecuado y preguntá si quiere agregarlo al carrito.',
              },
            ],
          },
        },
      },
      {
        value: 'product_catalog',
        icon: 'i-lucide-package',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.PRODUCT_CATALOG.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.PRODUCT_CATALOG.DESCRIPTION'
        ),
        catalogType: 'products',
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.PRODUCT_CATALOG.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.PRODUCT_CATALOG.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.PRODUCT_CATALOG.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá qué producto le interesa.',
              },
              {
                type: 'product_search',
                title: 'Buscar producto',
                product_query: '{{user_input}}',
                include_media: true,
              },
              {
                type: 'action',
                title: 'Detalles',
                instruction:
                  'Describí el producto encontrado con precio, disponibilidad y promociones si aplica.',
              },
            ],
          },
        },
      },
      {
        value: 'real_estate',
        icon: 'i-lucide-home',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.REAL_ESTATE.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.REAL_ESTATE.DESCRIPTION'),
        catalogType: 'real_estate',
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.REAL_ESTATE.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.REAL_ESTATE.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.REAL_ESTATE.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá qué tipo de propiedad busca.',
              },
              {
                type: 'action',
                title: 'Requisitos',
                instruction:
                  'Preguntá presupuesto, zona, cantidad de dormitorios y baños.',
              },
              {
                type: 'product_search',
                title: 'Buscar propiedad',
                product_query: '{{user_input}}',
                include_media: true,
              },
              {
                type: 'action',
                title: 'Agendar visita',
                instruction: 'Ofrecé agendar una visita presencial o virtual.',
              },
            ],
          },
        },
      },
      {
        value: 'aftersales',
        icon: 'i-lucide-refresh-cw',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.AFTERSALES.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.AFTERSALES.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AFTERSALES.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AFTERSALES.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AFTERSALES.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.6,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá cómo fue su experiencia con la compra.',
              },
              {
                type: 'action',
                title: 'Soporte',
                instruction:
                  'Ofrecé ayuda con cualquier problema o duda post-venta.',
              },
              {
                type: 'action',
                title: 'Encuesta',
                instruction:
                  'Preguntá si quiere dejar una valoración o recomendación.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'appointments',
    label: t('AI_AGENTS.WIZARD.GROUPS.APPOINTMENTS'),
    items: [
      {
        value: 'reception',
        icon: 'i-lucide-bell',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.RECEPTION.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.RECEPTION.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECEPTION.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECEPTION.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECEPTION.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al visitante y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Dirección',
                instruction:
                  'Dirigí al visitante al departamento o persona correcta.',
              },
            ],
          },
        },
      },
      {
        value: 'appointment_booking',
        icon: 'i-lucide-calendar',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.APPOINTMENT_BOOKING.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.APPOINTMENT_BOOKING.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.APPOINTMENT_BOOKING.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.APPOINTMENT_BOOKING.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.APPOINTMENT_BOOKING.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá qué servicio quiere agendar.',
              },
              {
                type: 'action',
                title: 'Datos',
                instruction:
                  'Pedí nombre, fecha y hora preferida para la cita.',
              },
              {
                type: 'action',
                title: 'Confirmación',
                instruction:
                  'Confirmá la cita y enviá un resumen de los datos.',
              },
            ],
          },
        },
      },
      {
        value: 'reservations',
        icon: 'i-lucide-ticket',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.RESERVATIONS.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.RESERVATIONS.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESERVATIONS.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESERVATIONS.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESERVATIONS.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá qué quiere reservar.',
              },
              {
                type: 'action',
                title: 'Detalles',
                instruction: 'Pedí fecha, cantidad de personas y preferencias.',
              },
              {
                type: 'action',
                title: 'Confirmación',
                instruction: 'Confirmá la reserva y enviá un resumen.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'marketing',
    label: t('AI_AGENTS.WIZARD.GROUPS.MARKETING'),
    items: [
      {
        value: 'marketing',
        icon: 'i-lucide-megaphone',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.MARKETING.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.MARKETING.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.MARKETING.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.MARKETING.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.MARKETING.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.9,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y presentá la promoción o campaña activa.',
              },
              {
                type: 'action',
                title: 'Interés',
                instruction:
                  'Preguntá si le interesa conocer más detalles o participar.',
              },
              {
                type: 'action',
                title: 'Acción',
                instruction:
                  'Invitá al cliente a realizar una acción: comprar, registrarse o compartir.',
              },
            ],
          },
        },
      },
      {
        value: 'customer_success',
        icon: 'i-lucide-heart-handshake',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUCCESS.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUCCESS.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUCCESS.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUCCESS.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.CUSTOMER_SUCCESS.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al cliente y preguntá cómo está usando el producto o servicio.',
              },
              {
                type: 'action',
                title: 'Valoración',
                instruction:
                  'Preguntá si está satisfecho o si necesita ayuda para sacar más provecho.',
              },
              {
                type: 'action',
                title: 'Upsell',
                instruction:
                  'Ofrecé funcionalidades premium o planes superiores si son relevantes.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'operations',
    label: t('AI_AGENTS.WIZARD.GROUPS.OPERATIONS'),
    items: [
      {
        value: 'collections',
        icon: 'i-lucide-dollar-sign',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.COLLECTIONS.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.COLLECTIONS.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.COLLECTIONS.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.COLLECTIONS.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.COLLECTIONS.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.5,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Identificación',
                instruction:
                  'Pedí el nombre o número de cliente para verificar la cuenta.',
              },
              {
                type: 'action',
                title: 'Información',
                instruction:
                  'Informá el monto adeudado y las opciones de pago disponibles.',
              },
              {
                type: 'action',
                title: 'Compromiso',
                instruction:
                  'Pedí una fecha de pago o confirmá si ya realizó el pago.',
              },
            ],
          },
        },
      },
      {
        value: 'hr',
        icon: 'i-lucide-users',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.HR.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.HR.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t('AI_AGENTS.WIZARD.SPECIALTIES.HR.SYSTEM_PROMPT'),
          instructions: t('AI_AGENTS.WIZARD.SPECIALTIES.HR.INSTRUCTIONS'),
          welcome_message: t('AI_AGENTS.WIZARD.SPECIALTIES.HR.WELCOME_MESSAGE'),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al empleado y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Consulta',
                instruction:
                  'Respondé consultas sobre beneficios, vacaciones, nómina o políticas de la empresa.',
              },
            ],
          },
        },
      },
      {
        value: 'recruitment',
        icon: 'i-lucide-user-plus',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.RECRUITMENT.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.RECRUITMENT.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECRUITMENT.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECRUITMENT.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RECRUITMENT.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saluda al candidato y preguntá por qué puesto está interesado.',
              },
              {
                type: 'action',
                title: 'Screening',
                instruction:
                  'Hacé preguntas iniciales sobre experiencia, disponibilidad y expectativas salariales.',
              },
              {
                type: 'action',
                title: 'Siguiente paso',
                instruction:
                  'Informá el proceso de selección y agenda una entrevista si corresponde.',
              },
            ],
          },
        },
      },
      {
        value: 'knowledge_base',
        icon: 'i-lucide-book-open',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.KNOWLEDGE_BASE.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.KNOWLEDGE_BASE.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.KNOWLEDGE_BASE.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.KNOWLEDGE_BASE.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.KNOWLEDGE_BASE.WELCOME_MESSAGE'
          ),
          response_level: 4,
          temperature: 0.4,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Recibir consulta',
                instruction:
                  'Recibí la pregunta del usuario y buscá la respuesta en la base de conocimiento.',
              },
              {
                type: 'action',
                title: 'Responder',
                instruction:
                  'Respondé con información precisa basada en los documentos cargados. Si no encontrás la respuesta, indicá que no tenés esa información.',
              },
            ],
          },
        },
      },
      {
        value: 'automation',
        icon: 'i-lucide-zap',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.AUTOMATION.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.AUTOMATION.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AUTOMATION.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AUTOMATION.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AUTOMATION.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.5,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Trigger',
                instruction:
                  'Detectá el evento o comando que activa la automatización.',
              },
              {
                type: 'action',
                title: 'Ejecutar',
                instruction: 'Realizá la acción automatizada correspondiente.',
              },
              {
                type: 'action',
                title: 'Confirmar',
                instruction:
                  'Informá al usuario que la acción fue completada exitosamente.',
              },
            ],
          },
        },
      },
      {
        value: 'ai_supervisor',
        icon: 'i-lucide-bot',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.AI_SUPERVISOR.LABEL'),
        description: t(
          'AI_AGENTS.WIZARD.SPECIALTIES.AI_SUPERVISOR.DESCRIPTION'
        ),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AI_SUPERVISOR.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AI_SUPERVISOR.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.AI_SUPERVISOR.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.3,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Monitoreo',
                instruction:
                  'Analizá la conversación entre el cliente y el agente IA en tiempo real.',
              },
              {
                type: 'condition',
                title: 'Calidad',
                condition_text:
                  'La respuesta del agente IA es inadecuada o incorrecta',
                true_action: 'Intervení corrigiendo o transferí a humano',
                false_action: 'Continuá monitoreando',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'health_wellness',
    label: t('AI_AGENTS.WIZARD.GROUPS.HEALTH_WELLNESS'),
    items: [
      {
        value: 'healthcare',
        icon: 'i-lucide-stethoscope',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.HEALTHCARE.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.HEALTHCARE.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HEALTHCARE.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HEALTHCARE.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.HEALTHCARE.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al paciente y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Servicio',
                instruction: 'Preguntá qué servicio o especialidad necesita.',
              },
              {
                type: 'action',
                title: 'Agendar',
                instruction:
                  'Ofrecé agendar una cita con fecha y hora preferida.',
              },
            ],
          },
        },
      },
      {
        value: 'fitness',
        icon: 'i-lucide-dumbbell',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.FITNESS.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.FITNESS.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.FITNESS.SYSTEM_PROMPT'
          ),
          instructions: t('AI_AGENTS.WIZARD.SPECIALTIES.FITNESS.INSTRUCTIONS'),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.FITNESS.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al visitante y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Interés',
                instruction:
                  'Preguntá si busca membresía, clase de prueba o información general.',
              },
              {
                type: 'action',
                title: 'Agendar',
                instruction: 'Ofrecé agendar una visita o clase de prueba.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'hospitality',
    label: t('AI_AGENTS.WIZARD.GROUPS.HOSPITALITY'),
    items: [
      {
        value: 'restaurant',
        icon: 'i-lucide-utensils-crossed',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.RESTAURANT.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.RESTAURANT.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESTAURANT.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESTAURANT.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.RESTAURANT.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al cliente y preguntá si quiere hacer un pedido, reservar una mesa o ver el menú.',
              },
              {
                type: 'action',
                title: 'Atender',
                instruction:
                  'Tomá el pedido, la reserva o mostrá el menú según lo que solicite.',
              },
              {
                type: 'action',
                title: 'Confirmar',
                instruction: 'Confirmá el pedido o reserva y agradecé.',
              },
            ],
          },
        },
      },
      {
        value: 'tourism',
        icon: 'i-lucide-plane',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.TOURISM.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.TOURISM.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.TOURISM.SYSTEM_PROMPT'
          ),
          instructions: t('AI_AGENTS.WIZARD.SPECIALTIES.TOURISM.INSTRUCTIONS'),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.TOURISM.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.8,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al huésped y preguntá en qué puedes ayudarle.',
              },
              {
                type: 'action',
                title: 'Interés',
                instruction:
                  'Preguntá si busca reservar habitación, paquete o información de destino.',
              },
              {
                type: 'action',
                title: 'Reservar',
                instruction:
                  'Ayudá con la reserva y ofrecé información adicional del destino.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'education',
    label: t('AI_AGENTS.WIZARD.GROUPS.EDUCATION'),
    items: [
      {
        value: 'education',
        icon: 'i-lucide-graduation-cap',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.EDUCATION.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.EDUCATION.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.EDUCATION.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.EDUCATION.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.EDUCATION.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.7,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al visitante y preguntá si es estudiante o padre de familia.',
              },
              {
                type: 'action',
                title: 'Interés',
                instruction:
                  'Preguntá sobre qué curso, programa o información necesita.',
              },
              {
                type: 'action',
                title: 'Informar',
                instruction:
                  'Brindá información de cursos, requisitos, costos y próximas fechas de inscripción.',
              },
            ],
          },
        },
      },
    ],
  },
  {
    key: 'professional_services',
    label: t('AI_AGENTS.WIZARD.GROUPS.PROFESSIONAL_SERVICES'),
    items: [
      {
        value: 'legal',
        icon: 'i-lucide-scale',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.LEGAL.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.LEGAL.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t('AI_AGENTS.WIZARD.SPECIALTIES.LEGAL.SYSTEM_PROMPT'),
          instructions: t('AI_AGENTS.WIZARD.SPECIALTIES.LEGAL.INSTRUCTIONS'),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LEGAL.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.5,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al visitante y preguntá en qué área legal necesita ayuda.',
              },
              {
                type: 'action',
                title: 'Consulta',
                instruction:
                  'Brindá información general sobre servicios y honorarios. No des asesoramiento legal específico.',
              },
              {
                type: 'action',
                title: 'Agendar',
                instruction:
                  'Ofrecé agendar una consulta con un abogado especializado.',
              },
            ],
          },
        },
      },
      {
        value: 'insurance',
        icon: 'i-lucide-shield-check',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.INSURANCE.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.INSURANCE.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.INSURANCE.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.INSURANCE.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.INSURANCE.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.6,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al cliente y preguntá qué tipo de seguro necesita.',
              },
              {
                type: 'action',
                title: 'Cotizar',
                instruction:
                  'Recopilá información necesaria para una cotización preliminar.',
              },
              {
                type: 'action',
                title: 'Seguir',
                instruction:
                  'Ofrecé agendar una llamada con un asesor para finalizar la cotización.',
              },
            ],
          },
        },
      },
      {
        value: 'logistics',
        icon: 'i-lucide-truck',
        label: t('AI_AGENTS.WIZARD.SPECIALTIES.LOGISTICS.LABEL'),
        description: t('AI_AGENTS.WIZARD.SPECIALTIES.LOGISTICS.DESCRIPTION'),
        catalogType: null,
        template: {
          system_prompt: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LOGISTICS.SYSTEM_PROMPT'
          ),
          instructions: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LOGISTICS.INSTRUCTIONS'
          ),
          welcome_message: t(
            'AI_AGENTS.WIZARD.SPECIALTIES.LOGISTICS.WELCOME_MESSAGE'
          ),
          response_level: 3,
          temperature: 0.6,
          config: {
            flow_steps: [
              {
                type: 'action',
                title: 'Saludo',
                instruction:
                  'Saludá al cliente y preguntá si necesita rastrear un envío, cotizar o información general.',
              },
              {
                type: 'action',
                title: 'Atender',
                instruction:
                  'Ayudá con el rastreo, cotización o información solicitada.',
              },
              {
                type: 'action',
                title: 'Confirmar',
                instruction:
                  'Confirmá la información brindada y ofrecé ayuda adicional.',
              },
            ],
          },
        },
      },
    ],
  },
]);

const catalogTypeOptions = [
  {
    value: 'products',
    label: t('AI_AGENTS.CATALOG.TYPES.PRODUCTS'),
    icon: 'i-lucide-package',
  },
  {
    value: 'services',
    label: t('AI_AGENTS.CATALOG.TYPES.SERVICES'),
    icon: 'i-lucide-wrench',
  },
  {
    value: 'vehicles',
    label: t('AI_AGENTS.CATALOG.TYPES.VEHICLES'),
    icon: 'i-lucide-car',
  },
  {
    value: 'real_estate',
    label: t('AI_AGENTS.CATALOG.TYPES.REAL_ESTATE'),
    icon: 'i-lucide-home',
  },
];

const filteredGroups = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return specialtyGroups.value;
  return specialtyGroups.value
    .map(group => ({
      ...group,
      items: group.items.filter(
        item =>
          item.label.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q)
      ),
    }))
    .filter(group => group.items.length > 0);
});

const selectedSpecialtyData = computed(() => {
  return (
    specialtyGroups.value
      .flatMap(group => group.items)
      .find(i => i.value === selectedSpecialty.value) || null
  );
});

const needsCatalog = computed(() => {
  return selectedSpecialtyData.value?.catalogType !== null;
});

const selectSpecialty = value => {
  selectedSpecialty.value = value;
  const data = selectedSpecialtyData.value;
  if (data?.template) {
    agentName.value = data.label || '';
    agentDescription.value = data.description || '';
    if (data.catalogType) {
      selectedCatalogType.value = data.catalogType;
    }
  }
  step.value = 2;
};

const goBack = () => {
  step.value -= 1;
};

const reset = () => {
  step.value = 1;
  selectedSpecialty.value = null;
  agentName.value = '';
  agentDescription.value = '';
  selectedCatalogType.value = 'products';
  searchQuery.value = '';
  isCreating.value = false;
  createdAgentId.value = null;
  isSavingKnowledge.value = false;
  industryForm.value = {
    specialization: '',
    businessName: '',
    website: '',
    description: '',
  };
};

const createAgent = async () => {
  isCreating.value = true;
  try {
    const data = selectedSpecialtyData.value;
    const payload = {
      name: agentName.value,
      description: agentDescription.value,
      industry_template: selectedSpecialty.value,
      llm_provider: 'openai',
      llm_model: 'gpt-4o-mini',
    };

    if (data?.template) {
      payload.system_prompt = data.template.system_prompt;
      payload.instructions = data.template.instructions;
      payload.welcome_message = data.template.welcome_message;
      payload.response_level = data.template.response_level;
      payload.temperature = data.template.temperature;
      payload.config = data.template.config;
    }

    if (needsCatalog.value) {
      payload.catalog_type = selectedCatalogType.value;
    }

    const response = await store.dispatch('aiAgents/create', payload);
    if (response?.id) {
      useAlert(t('AI_AGENTS.MESSAGES.CREATE_SUCCESS'));
      createdAgentId.value = response.id;
      step.value = 3;
    } else {
      useAlert(t('AI_AGENTS.MESSAGES.CREATE_ERROR'));
    }
  } catch (error) {
    useAlert(t('AI_AGENTS.MESSAGES.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const buildIndustryContent = () => {
  const i = industryForm.value;
  const lines = [];
  if (i.specialization) lines.push(`Especialización: ${i.specialization}`);
  if (i.businessName) lines.push(`Nombre del negocio: ${i.businessName}`);
  if (i.website) lines.push(`Sitio web del negocio: ${i.website}`);
  if (i.description) lines.push(`Descripción del negocio: ${i.description}`);
  return lines.join('\n');
};

const saveKnowledge = async () => {
  if (!createdAgentId.value) return;
  isSavingKnowledge.value = true;
  try {
    const payload = {
      name: industryForm.value.businessName || agentName.value || 'Industria',
      source_type: 'text',
      content: buildIndustryContent(),
    };
    await AiAgentsAPI.createDocument(createdAgentId.value, payload);
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    const agentId = createdAgentId.value;
    reset();
    emit('close');
    await router.push({ name: 'ai_agent_show', params: { id: agentId } });
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  } finally {
    isSavingKnowledge.value = false;
  }
};

const skipKnowledge = async () => {
  const agentId = createdAgentId.value;
  reset();
  emit('close');
  if (agentId) {
    await router.push({ name: 'ai_agent_show', params: { id: agentId } });
  }
};

const close = () => {
  reset();
  emit('close');
};
</script>

<template>
  <div
    v-show="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="close"
  >
    <div
      class="overflow-y-auto bg-n-surface-1 shadow-xl transition-all"
      :class="[
        step === 1
          ? 'w-full h-[95vh] max-w-6xl p-6 rounded-xl'
          : 'w-full max-w-4xl max-h-[90vh] p-6 rounded-xl',
      ]"
    >
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-semibold">
            {{ t('AI_AGENTS.WIZARD.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('AI_AGENTS.WIZARD.STEP', { step, total: 3 }) }}
          </p>
        </div>
        <button class="text-n-slate-10 hover:text-n-slate-12" @click="close">
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Step 1: Specialty Selection -->
      <div v-if="step === 1">
        <p class="text-sm text-n-slate-10 mb-4">
          {{ t('AI_AGENTS.WIZARD.SELECT_SPECIALTY') }}
        </p>

        <!-- Search bar -->
        <div
          class="flex items-center gap-2 px-3 py-2.5 mb-6 rounded-xl border border-n-slate-6 bg-n-surface-2 focus-within:border-n-brand focus-within:ring-1 focus-within:ring-n-brand/30"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4 text-n-slate-9 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" stroke-linecap="round" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="flex-1 text-sm bg-transparent text-n-slate-12 placeholder:text-n-slate-9 outline-none"
            :placeholder="t('AI_AGENTS.WIZARD.SEARCH_PLACEHOLDER')"
          />
          <button
            v-if="searchQuery"
            class="text-n-slate-9 hover:text-n-slate-12 flex-shrink-0"
            @click="searchQuery = ''"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M18 6 6 18M6 6l12 12"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- No results -->
        <div
          v-if="filteredGroups.length === 0 && searchQuery"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <span class="i-lucide-search-x w-10 h-10 text-n-slate-8 mb-3" />
          <p class="text-sm text-n-slate-10">
            {{
              t('AI_AGENTS.WIZARD.NO_SEARCH_RESULTS', { query: searchQuery })
            }}
          </p>
        </div>

        <div v-for="group in filteredGroups" :key="group.key" class="mb-8">
          <div
            class="flex items-center gap-2 mb-4 pb-2 border-b border-n-slate-5"
          >
            <h3
              class="text-xs font-bold uppercase tracking-wider text-n-slate-11"
            >
              {{ group.label }}
            </h3>
            <span
              class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-n-slate-3 text-n-slate-9"
            >
              {{ group.items.length }}
            </span>
          </div>
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
          >
            <button
              v-for="item in group.items"
              :key="item.value"
              class="group flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all duration-200 text-center hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.02]"
              :class="
                selectedSpecialty === item.value
                  ? 'border-n-brand bg-n-brand/10 text-n-brand shadow-md'
                  : 'border-n-slate-5 hover:border-n-brand/40 hover:bg-n-blue-2 text-n-slate-11'
              "
              @click="selectSpecialty(item.value)"
            >
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200"
                :class="
                  selectedSpecialty === item.value
                    ? 'bg-n-brand/15'
                    : 'bg-n-slate-3 group-hover:bg-n-brand/10'
                "
              >
                <span
                  :class="item.icon"
                  class="w-6 h-6 transition-colors duration-200"
                />
              </div>
              <div class="text-sm font-semibold leading-tight">
                {{ item.label }}
              </div>
              <div class="text-[11px] leading-relaxed opacity-70 line-clamp-2">
                {{ item.description }}
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Basic Configuration -->
      <div v-else-if="step === 2">
        <div class="mb-4">
          <button
            class="text-sm text-n-brand hover:text-n-brand/80 flex items-center gap-1"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left w-4 h-4" />
            {{ t('AI_AGENTS.WIZARD.BACK') }}
          </button>
        </div>

        <div class="grid gap-4">
          <div
            class="flex items-center gap-3 p-4 rounded-xl border border-n-slate-6 bg-n-surface-2"
          >
            <span
              :class="selectedSpecialtyData?.icon"
              class="w-8 h-8 text-n-brand"
            />
            <div>
              <div class="text-sm font-semibold">
                {{ selectedSpecialtyData?.label }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ selectedSpecialtyData?.description }}
              </div>
            </div>
          </div>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.FORM.NAME')
            }}</span>
            <input
              v-model="agentName"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.FORM.NAME_PLACEHOLDER')"
            />
          </label>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.FORM.DESCRIPTION')
            }}</span>
            <input
              v-model="agentDescription"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.FORM.DESCRIPTION')"
            />
          </label>

          <!-- Catalog Type Selector (if applicable) -->
          <div v-if="needsCatalog" class="grid gap-2">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.CATALOG_TYPE_TITLE')
            }}</span>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                v-for="opt in catalogTypeOptions"
                :key="opt.value"
                class="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-center"
                :class="
                  selectedCatalogType === opt.value
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'border-n-slate-6 hover:border-n-slate-8 text-n-slate-11'
                "
                @click="selectedCatalogType = opt.value"
              >
                <span :class="opt.icon" class="w-6 h-6" />
                <span class="text-xs font-semibold">{{ opt.label }}</span>
              </button>
            </div>
          </div>

          <!-- Info for pre-configured templates -->
          <div
            v-if="selectedSpecialtyData?.template"
            class="p-4 rounded-lg border border-n-emerald-9/20 bg-n-emerald-9/5"
          >
            <div class="flex items-center gap-2 mb-2">
              <span class="i-lucide-sparkles w-4 h-4 text-n-emerald-9" />
              <span class="text-sm font-semibold text-n-emerald-11">{{
                t('AI_AGENTS.WIZARD.PRE_CONFIGURED')
              }}</span>
            </div>
            <ul class="text-xs text-n-slate-10 space-y-1">
              <li>{{ t('AI_AGENTS.WIZARD.PRE_CONFIGURED_IDENTITY') }}</li>
              <li>{{ t('AI_AGENTS.WIZARD.PRE_CONFIGURED_STYLE') }}</li>
              <li>{{ t('AI_AGENTS.WIZARD.PRE_CONFIGURED_FLOW') }}</li>
              <li>{{ t('AI_AGENTS.WIZARD.PRE_CONFIGURED_PARAMETERS') }}</li>
            </ul>
          </div>

          <!-- Info for custom -->
          <div
            v-else
            class="p-4 rounded-lg border border-n-amber-9/20 bg-n-amber-9/5"
          >
            <div class="flex items-center gap-2 mb-2">
              <span class="i-lucide-info w-4 h-4 text-n-amber-9" />
              <span class="text-sm font-semibold text-n-amber-11">{{
                t('AI_AGENTS.WIZARD.CUSTOM_MODE')
              }}</span>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ t('AI_AGENTS.WIZARD.CUSTOM_MODE_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg border border-n-slate-6"
            @click="goBack"
          >
            {{ t('AI_AGENTS.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50"
            :disabled="!agentName.trim() || isCreating"
            @click="createAgent"
          >
            {{
              isCreating
                ? t('AI_AGENTS.WIZARD.CREATING')
                : t('AI_AGENTS.CREATE')
            }}
          </button>
        </div>
      </div>

      <!-- Step 3: Knowledge / Industry Info -->
      <div v-else-if="step === 3">
        <div
          class="flex items-center gap-3 p-4 rounded-xl border border-n-emerald-9/20 bg-n-emerald-9/5 mb-6"
        >
          <span
            class="i-lucide-check-circle w-6 h-6 text-n-emerald-9 flex-shrink-0"
          />
          <div>
            <p class="text-sm font-semibold text-n-emerald-11">
              {{ t('AI_AGENTS.WIZARD.SUCCESS_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('AI_AGENTS.WIZARD.KNOWLEDGE_STEP_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <h3 class="text-base font-semibold mb-4 flex items-center gap-2">
          <span class="i-lucide-briefcase w-5 h-5 text-n-brand" />
          {{ t('AI_AGENTS.KNOWLEDGE.TYPE_INDUSTRY') }}
        </h3>

        <div class="grid gap-4">
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION')
            }}</span>
            <input
              v-model="industryForm.specialization"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION_PLACEHOLDER')"
            />
          </label>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME')
            }}</span>
            <input
              v-model="industryForm.businessName"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME_PLACEHOLDER')"
            />
          </label>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE')
            }}</span>
            <input
              v-model="industryForm.website"
              type="url"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="
                t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE_PLACEHOLDER')
              "
            />
          </label>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION')
            }}</span>
            <textarea
              v-model="industryForm.description"
              rows="4"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 resize-none"
              :placeholder="
                t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION_PLACEHOLDER')
              "
            />
          </label>
        </div>

        <div class="flex items-center justify-between mt-6">
          <button
            class="px-4 py-2 text-sm text-n-slate-10 hover:text-n-slate-12"
            @click="skipKnowledge"
          >
            {{ t('AI_AGENTS.WIZARD.SKIP_KNOWLEDGE') }}
          </button>
          <button
            class="px-4 py-2.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50 flex items-center gap-2"
            :disabled="isSavingKnowledge"
            @click="saveKnowledge"
          >
            <span
              v-if="isSavingKnowledge"
              class="i-lucide-loader-2 w-4 h-4 animate-spin"
            />
            <span v-else class="i-lucide-save w-4 h-4" />
            {{ t('AI_AGENTS.WIZARD.SAVE_AND_CONTINUE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
